import React, { useEffect, useState } from 'react';
import { Switch, Route, Redirect, useHistory, useLocation, Link } from 'react-router-dom';
import { createStyles } from 'utils/createStyle';

import { Navigation } from './components/Navigation';
import { StickyHeader } from './components/StickyHeader';
import { Footer } from './components/Footer';
import { Routes } from 'routes';
import { useFeatureFlags, FeatureFlagTypes } from 'common/hooks/useFeatureFlags';
import { getSections, SectionConfig } from './nav-items';
import { useIsScripware } from 'hooks/useIsScripware';
import { ReactComponent as RaiseRightLogo } from '../../media/images/RaiseRightLogo.svg';
import { ReactComponent as FlipGiveLogo } from '../../media/images/flipgive/FlipGiveLogo.svg';
import { IS_FLIPGIVE } from 'common/env';
import { colors } from 'styles/settings';
import { FullScreenOverlay } from 'features/shop-base/OrgChangeOverlay/FullScreenOverlay';
import { useSelector } from 'react-redux';
import { selectOrgSwitchState } from 'common/features/store/duck/home/duck';
import { useRootSelector } from 'rootStore';
import { withAuth } from 'features/auth/components/withAuth';

const HomeScreen = React.lazy(() => import('./screens/HomeScreen'));
const PackingSlipsScreen = React.lazy(() => import('./screens/PackingSlipsScreen'));
const AllReportsScreen = React.lazy(() => import('./screens/AllReportsScreen'));
const ParticipantAccountsScreen = React.lazy(() => import('./screens/ParticipantAccountsScreen'));
const InviteParticipantsScreen = React.lazy(() => import('./screens/InviteParticipantsScreen'));
const OrganizationProfileScreen = React.lazy(() => import('./screens/OrganizationProfileScreen'));
const CoordinatorRolesScreen = React.lazy(() => import('./screens/CoordinatorRolesScreen'));
const OrganizationPINResetScreen = React.lazy(() => import('./screens/OrganizationPINResetScreen'));
const LocalProductsScreen = React.lazy(() => import('./screens/LocalProductsScreen'));
const LocalSuppliersScreen = React.lazy(() => import('./screens/LocalSuppliersScreen'));
const NotificationsScreen = React.lazy(() => import('./screens/NotificationsScreen'));
const SCRIPWAREScreen = React.lazy(() => import('./screens/SCRIPWAREScreen'));
const PaymentChangeFormScreen = React.lazy(() => import('./screens/PaymentChangeFormScreen'));
const PaymentTypesScreen = React.lazy(() => import('./screens/PaymentTypesScreen'));
const ResourceCenterScreen = React.lazy(() => import('./screens/ResourceCenterScreen'));
const CustomerSupportScreen = React.lazy(() => import('./screens/CustomerSupportScreen'));
const EnterOrdersScreen = React.lazy(() => import('./screens/EnterOrdersScreen'));
const OrderQueueScreen = React.lazy(() => import('./screens/OrderQueueScreen'));
const POManagerScreen = React.lazy(() => import('./screens/POManagerScreen'));
const ParticipantOrderFormScreen = React.lazy(() => import('./screens/ParticipantOrderFormScreen'));
const OrderHistoryScreen = React.lazy(() => import('./screens/OrderHistory'));
const OrganizationSettingsScreen = React.lazy(() => import('./screens/OrganizationSettingsScreen'));

export const CoordinatorDashboard = withAuth('/shop/account/coordinator-dashboard-new/home')(() => {
    const homeDetails = useRootSelector((state) => state?.store?.home?.homeDetails);
    const { isEnabled, flags } = useFeatureFlags();
    const location = useLocation();
    const history = useHistory();
    const [sections, setSections] = useState<SectionConfig[] | null>(null);
    const isScripwareUser = useIsScripware();
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isNavCollapsed, setIsNavCollapsed] = useState(true);

    useEffect(() => {
        const onScroll = () => setHasScrolled(window.scrollY > 0);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Lock body scroll while the mobile nav drawer is open so the page
    // underneath doesn't scroll behind the overlay.
    useEffect(() => {
        if (!isNavCollapsed) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isNavCollapsed]);

    const userRole = useRootSelector((s) => s.store.account.role);
    const isInviteHubEnabled = isEnabled(FeatureFlagTypes.enable_coordinator_invite_hub_web);

    useEffect(() => {
        const sections_ = getSections(isScripwareUser, userRole, isInviteHubEnabled);
        setSections(sections_);
    }, [isScripwareUser, userRole, isInviteHubEnabled]);

    const styleClasses = createStyles({
        mainContent: [
            'bg-white flex w-full min-w-0 overflow-x-hidden',
            {
                minHeight: '80vh',
            },
        ],
        layout: {
            display: 'grid',
            // Responsive grid: single column on sm/md/lg, two columns on xl+
            gridTemplateColumns: '1fr',
            gridTemplateRows: '62px 1fr',
            gridTemplateAreas: `
                "headerContainer"
                "mainContentContainer"
            `,
            '@media (min-width: 1024px)': {
                gridTemplateColumns: '260px 1fr',
                gridTemplateAreas: `
                    "logoContainer headerContainer"
                    "navigationContainer mainContentContainer"
                    "navigationContainer stickyHeader"
                `,
            },
            minHeight: '100vh',
            '@supports (min-height: 100dvh)': {
                minHeight: '100dvh',
            },
        },
        navigationContainer: [
            'px-1 pt-4 mt-[2px] pb-0 border-r border-grey-line',
            {
                gridArea: 'navigationContainer',
                overflowY: 'auto',
                height: 'calc(100vh - 62px)',
                '@supports (height: 100dvh)': {
                    height: 'calc(100dvh - 62px)',
                },
                position: 'fixed',
                top: '60px',
                left: '0',
                width: '100%',
                backgroundColor: '#fff',
                zIndex: 40,
                transition: 'transform 0.3s ease-in-out',
                transform: isNavCollapsed ? 'translateX(-100%)' : 'translateX(0)',
                '@media (min-width: 1024px)': {
                    // Sticky keeps the sidebar (and its sign-out footer) in view
                    // while the tall main content scrolls.
                    position: 'sticky',
                    top: '62px',
                    alignSelf: 'start',
                    transform: 'translateX(0)',
                },
            },
        ],

        logoContainer: [
            'pt-3 pl-6 border-r border-grey-line height-61',
            {
                gridArea: 'logoContainer',
                background: '#fff',
                height: '61px',
                width: '260px',
                zIndex: 2,
                display: 'none',
                '@media (min-width: 1024px)': {
                    display: 'block',
                },
            },
            hasScrolled ? 'border-b' : '',
        ],
        headerContainer: {
            gridArea: 'headerContainer',
            position: 'sticky',
            top: 0,
            zIndex: 30,
            background: '#fff',
        },
        mainContentContainer: [
            'pb-0',
            {
                gridArea: 'mainContentContainer',
                width: '100%',
                minWidth: 0,
                overflowX: 'hidden',
            },
        ],
        scrollView: {},
        footer: [
            'pt-4 pl-4',
            {
                borderTop: `1px solid ${colors.lineGrey}`,
            },
        ],
        hamburgerButton: [
            'p-3',
            {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '24px',
                color: colors.brand,
                '@media (min-width: 1024px)': {
                    display: 'none',
                },
            },
        ],
    });

    const isLoggedIn = useRootSelector((s) => !!s.store.account.userId);
    const isCoordinator = useRootSelector((s) => s.store.account.isCoordinator);

    useEffect(() => {
        if (!isLoggedIn) {
            history.push(Routes.Shop);
        }
    }, [isLoggedIn, isCoordinator]);

    useEffect(() => {
        if (Object.keys(flags).length === 0 || !homeDetails) {
            return;
        }

        if (!isEnabled(FeatureFlagTypes.enable_unified_coordinator_dashboard)) {
            if (isCoordinator) {
                history.push(Routes.ShopAccountSettingsCoordinatorTab);
            } else {
                history.push(Routes.ShopAccountSettingsDashboardTab);
            }
        } else {
            // CASE: You're not a coordinator to your currently selected org
            // _but_ you do have the feature flag enabled.
            if (!isCoordinator) {
                history.push(Routes.ShopAccountSettingsDashboardTab);
            }
        }
    }, [isEnabled, history, flags, isCoordinator, homeDetails]);

    const { showOverlay } = useSelector(selectOrgSwitchState);

    if (!sections) {
        return null;
    }

    if (showOverlay) {
        return <FullScreenOverlay />;
    }

    // clip, not hidden: hidden turns this into a scroll container and kills
    // every position:sticky inside (header and sidebar sign-out).
    return (
        <div data-recording-sensitive style={{ overflowX: 'clip' }}>
            <div className={styleClasses.layout}>
                <div className={styleClasses.logoContainer}>
                    <Link to={Routes.Shop}>
                        {IS_FLIPGIVE ? (
                            <FlipGiveLogo width="173" height="33" />
                        ) : (
                            <RaiseRightLogo width="196" height="37" />
                        )}
                    </Link>
                </div>

                <div className={`${styleClasses.navigationContainer} coord_dash_nav_container`}>
                    <Navigation
                        sections={sections}
                        onNavItemClick={() => setIsNavCollapsed(true)}
                    />
                </div>

                <div className={styleClasses.headerContainer}>
                    <StickyHeader
                        onMenuToggle={() => setIsNavCollapsed(!isNavCollapsed)}
                        isNavCollapsed={isNavCollapsed}
                    />
                </div>

                <div className={styleClasses.mainContentContainer}>
                    <div className={`${styleClasses.scrollView} coord_dash_scroll_main`}>
                        <div className={styleClasses.mainContent}>
                            <React.Suspense fallback={null}>
                                <Switch location={location}>
                                    <Route
                                        path={Routes.CoordinatorDashboardHome}
                                        component={HomeScreen}
                                    />

                                    <Route
                                        path={Routes.CoordinatorDashboardPackingSlips}
                                        component={PackingSlipsScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardAllReports}
                                        component={AllReportsScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardParticipantAccounts}
                                        component={ParticipantAccountsScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardInviteParticipants}
                                        component={InviteParticipantsScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardOrganizationProfile}
                                        component={OrganizationProfileScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardOrganizationSettings}
                                        component={OrganizationSettingsScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardCoordinatorRoles}
                                        component={CoordinatorRolesScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardOrganizationPINReset}
                                        component={OrganizationPINResetScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardLocalProducts}
                                        component={LocalProductsScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardLocalSuppliers}
                                        component={LocalSuppliersScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardNotifications}
                                        component={NotificationsScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardSCRIPWARE}
                                        component={SCRIPWAREScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardPaymentChangeForm}
                                        component={PaymentChangeFormScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardPaymentTypes}
                                        component={PaymentTypesScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardResourceCenter}
                                        component={ResourceCenterScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardCustomerSupport}
                                        component={CustomerSupportScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardEnterOrders}
                                        exact
                                        component={EnterOrdersScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardOrderQueue}
                                        exact
                                        component={OrderQueueScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardPOManager}
                                        exact
                                        component={POManagerScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardParticipantOrderForm}
                                        exact
                                        component={ParticipantOrderFormScreen}
                                    />
                                    <Route
                                        path={Routes.CoordinatorDashboardOrderHistory}
                                        exact
                                        component={OrderHistoryScreen}
                                    />

                                    <Redirect
                                        from={Routes.CoordinatorDashboardRoot}
                                        to={Routes.CoordinatorDashboardHome}
                                    />
                                </Switch>
                            </React.Suspense>
                        </div>

                        <div className={styleClasses.footer}>
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});
